import { Component, HostBinding, HostListener, AfterViewInit, OnDestroy, ElementRef, ViewChild, OnInit, ChangeDetectorRef } from '@angular/core';
import { CartService } from '../../../shared/services/cart.service';
import { FormControl } from '@angular/forms';
import { StorageService } from '../../../shared/services/storage.service';
import { AuthService, User } from '../../../shared/services/auth.service';
import { LanguageService } from '../../../shared/services/language.service';
import { ShippingConfigService } from '../../../core/services/shipping-config.service';
import { debounceTime, take, takeUntil, BehaviorSubject, combineLatest, startWith, distinctUntilChanged, switchMap, catchError, of, tap, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { CurrencyService, SupportedCurrency } from '../../../core/services/currency.service';
import { SettingsService } from '../../../shared/services/settings.service';
import { ProductComparisonService } from '../../../shared/services/product-comparison.service';

@Component({
  selector: 'app-navbar',
  standalone: false,
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit, AfterViewInit, OnDestroy {
  isDrawerOpen = false;
  @HostBinding('class.menu-open') get isMenuOpen(): boolean {
    return this.isDrawerOpen;
  }

  selectedDesktopMenuAction: 'CATEGORIES' | 'SALE' | 'NEW' = 'CATEGORIES';
  isCartOpen = false;
  public isCartClosing = false;
  isMobileSearchOpen = false;
  isAuthDropdownOpen = false;
  isCountrySelectorOpen = false;
  isNavbarCountryDropdownOpen = false;
  isNavbarCurrencyDropdownOpen = false;
  searchControl = new FormControl('');
  countrySearchTerm = '';
  selectedCountry: any = null;
  selectedCurrency: SupportedCurrency = 'EUR';
  countrySelectionMessage = '';
  @ViewChild('navbarCountrySearchInput') navbarCountrySearchInputElement?: ElementRef<HTMLInputElement>;
  private readonly COUNTRY_COOKIE_KEY = 'brocom_selected_country';
  private readonly COUNTRY_VISITED_KEY = 'brocom_country_visited';
  // Subject for language change events
  private languageChange$ = new Subject<string>();
  // Current language for flag display
  currentLanguageFlag = '🇬🇧';
  currentLanguageCode = 'pl';
  countries: any[] = [];
  protected variants: any = {};
  protected selectedVariants: any = {};
  filteredProducts: any[] = [];
  products: any[] = [];
  currentUser: User | null = null;
  isScrolled = false; // Track scroll state for logo blur effect
  private products$ = new BehaviorSubject<any[]>([]);

  // Add destroy subject for subscription management
  private destroy$ = new Subject<void>();

  // ViewChild for safer DOM access
  @ViewChild('mobileSearchInput', { static: false }) mobileSearchInput?: ElementRef<HTMLInputElement>;

  categories: any[] = [];
  isManufacturersModalOpen = false;
  isManufacturersModalMounted = false;
  manufacturers: string[] = [];
  readonly manufacturerLetters = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];
  selectedManufacturerLetter = '';
  manufacturerSearchTerm = '';
  isLoadingManufacturers = false;
  comparisonCount = 0;
  topBarAnnouncements: { id: number; text: string }[] = [];
  activeTopBarIndex = 0;
  private topBarRequest?: Subscription;

  // Responsive utilities
  isDesktop = false;
  isMobile = false;
  private resizeObserver!: ResizeObserver;
  private manufacturersModalCloseTimeout?: ReturnType<typeof setTimeout>;
  private manufacturersModalOpenFrames: number[] = [];

  constructor(
    public cartService: CartService,
    private readonly productsService: StorageService,
    public authService: AuthService,
    private router: Router,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef,
    public shippingConfigService: ShippingConfigService,
    public readonly currencyService: CurrencyService,
    private readonly settingsService: SettingsService,
    private readonly comparisonService: ProductComparisonService,
  ) { }

  get liveExchangeRate(): number {
    return this.currencyService.currentRate;
  }

  get currentLanguageBadge(): string {
    return this.currentLanguageCode === 'en'
      ? 'GB'
      : this.currentLanguageCode.slice(0, 2).toUpperCase();
  }

  get filteredManufacturers(): string[] {
    const searchTerm = this.manufacturerSearchTerm.trim().toLocaleLowerCase();
    return this.manufacturers.filter(name => {
      const matchesLetter =
        !this.selectedManufacturerLetter
        || this.selectedManufacturerLetter === '#'
        || name.toLocaleUpperCase().startsWith(this.selectedManufacturerLetter);
      const matchesSearch = !searchTerm || name.toLocaleLowerCase().includes(searchTerm);
      return matchesLetter && matchesSearch;
    });
  }

  get manufacturerEmptyStateMessage(): string {
    if (this.manufacturerSearchTerm.trim()) {
      return `Nie znaleziono producentów pasujących do „${this.manufacturerSearchTerm.trim()}”.`;
    }
    return `Brak producentów na literę ${this.selectedManufacturerLetter}.`;
  }

  ngOnInit(): void {
    this.comparisonCount = this.comparisonService.count;
    this.comparisonService.count$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.comparisonCount = count;
        this.cdr.markForCheck();
      });

    this.selectedCurrency = this.currencyService.currentCurrency;
    this.currencyService.currency$
      .pipe(takeUntil(this.destroy$))
      .subscribe((curr) => {
        this.selectedCurrency = curr;
        this.cdr.markForCheck();
      });
    // Set language from saved country FIRST, before any other initialization
    // This prevents the English flash on page reload
    this.applyLanguageFromSavedCountry();

    // Initialize language flag from localStorage
    this.updateCurrentLanguageFlag();

    this.getProducts();
    this.getSearchValue();
    // Initialize scroll state
    this.isScrolled = window.scrollY > 50;

    // Load countries dynamically from ShippingConfigService (no hardcoding)
    this.shippingConfigService.getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe((countryList) => {
        this.countries = (countryList || []).map((c) => ({
          code: c.code,
          name: c.name,
          flag: this.getCountryFlag(c.code),
          currency: (c.code || '').toUpperCase() === 'PL' ? 'PLN' : 'EUR',
          currencySymbol: (c.code || '').toUpperCase() === 'PL' ? 'zł' : '€',
        }));
        this.checkFirstTimeVisitor();
        this.cdr.markForCheck();
      });

    // Subscribe to authentication changes with proper cleanup
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
      });

    // Subscribe to language change events from LanguageService
    this.languageService.languageChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((languageCode: string) => {
        this.updateCurrentLanguageFlag();
      });

    // Subscribe to openCartDrawer events from CartService
    this.cartService.openCartDrawer$
      .pipe(takeUntil(this.destroy$))
      .subscribe((open) => {
        if (open) {
          this.openCart();
        }
      });
  }

  getCountryFlag(countryCode: string): string {
    if (!countryCode || countryCode.length !== 2) return '🌐';
    try {
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
    } catch (_) {
      return '🌐';
    }
  }

  // Update the language flag and announcement using the canonical language state.
  updateCurrentLanguageFlag(): void {
    const selectedLanguage = this.languageService.currentLanguage;
    this.currentLanguageCode = selectedLanguage;
    this.loadTopBarAnnouncements(
      selectedLanguage === 'fr' ? 'fr' : selectedLanguage === 'pl' ? 'pl' : 'en',
    );

    if (selectedLanguage === 'fr') {
      this.currentLanguageFlag = '🇫🇷';
    } else if (selectedLanguage === 'pl') {
      this.currentLanguageFlag = '🇵🇱';
    } else {
      this.currentLanguageFlag = '🇬🇧';
    }

    // Force change detection for language-state updates from the shared subject.
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  @HostListener('window:storage', ['$event'])
  onLanguagePreferenceChanged(event: StorageEvent): void {
    const language = event.newValue;
    if (
      event.key !== 'selectedLanguage'
      || !language
      || !['en', 'fr', 'pl'].includes(language)
    ) {
      return;
    }

    this.languageService.emitLanguageChange(language);
  }

  // Apply language from saved country - called synchronously in ngOnInit
  private applyLanguageFromSavedCountry(): void {
    // Language is now managed independently by the language switcher
    // No automatic language change based on country
  }

  private async checkFirstTimeVisitor(): Promise<void> {
    const hasVisited = this.getCookie(this.COUNTRY_VISITED_KEY);
    const savedCountry = localStorage.getItem('brocom_selected_country') || this.getCookie(this.COUNTRY_COOKIE_KEY);

    if (savedCountry) {
      try {
        const parsed = JSON.parse(savedCountry);
        const code = parsed?.code || parsed;
        const match = this.countries.find(c => c.code.toUpperCase() === code.toUpperCase());
        if (match) {
          this.selectedCountry = match;
        } else if (code) {
          this.selectedCountry = {
            code: code.toUpperCase(),
            name: parsed.name || code,
            flag: this.getCountryFlag(code),
            currency: (code || '').toUpperCase() === 'PL' ? 'PLN' : 'EUR',
            currencySymbol: (code || '').toUpperCase() === 'PL' ? 'zł' : '€',
          };
        }
      } catch (error) {
        this.selectedCountry = this.countries.find(c => c.code === 'PL') || this.countries[0] || null;
      }
    } else {
      // First time visitor: Detect location and language automatically
      const detection = await this.languageService.detectLocationAndLanguage();
      const detectedCode = (detection.countryCode || (detection.language === 'pl' ? 'PL' : (detection.language === 'fr' ? 'FR' : 'DE'))).toUpperCase();
      const match = this.countries.find(c => c.code.toUpperCase() === detectedCode);
      if (match) {
        this.selectedCountry = match;
      } else {
        this.selectedCountry = {
          code: detectedCode,
          name: detectedCode,
          flag: this.getCountryFlag(detectedCode),
          currency: detectedCode === 'PL' ? 'PLN' : 'EUR',
          currencySymbol: detectedCode === 'PL' ? 'zł' : '€',
        };
      }

      this.selectedCurrency = detectedCode === 'PL' ? 'PLN' : 'EUR';
      this.currencyService.onCountryChange(detectedCode);
      this.cdr.markForCheck();
    }

    // Don't auto-open country selector on checkout success page or other checkout pages
    const currentUrl = this.router.url;
    const isCheckoutRelatedPage = currentUrl.includes('/checkout') || currentUrl.includes('/success');

    // If user hasn't visited before AND hasn't selected a country AND not on checkout pages, auto-open the country selector
    if (!hasVisited && !savedCountry && !isCheckoutRelatedPage) {
      setTimeout(() => {
        this.openCountrySelectorSmooth();
      }, 1000); // Wait 1 second after page load for smooth experience
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.topBarRequest?.unsubscribe();

    if (this.manufacturersModalCloseTimeout) {
      clearTimeout(this.manufacturersModalCloseTimeout);
    }
    this.cancelManufacturersModalOpenFrames();

    // Clear any pending timeouts
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
    }
  }

  @HostListener('window:keydown.escape')
  closeManufacturersModal(): void {
    if (!this.isManufacturersModalMounted) {
      return;
    }

    this.cancelManufacturersModalOpenFrames();
    this.isManufacturersModalOpen = false;
    this.manufacturerSearchTerm = '';
    this.selectedManufacturerLetter = '';
    this.manufacturersModalCloseTimeout = setTimeout(() => {
      this.isManufacturersModalMounted = false;
      this.cdr.markForCheck();
    }, 220);
  }

  openManufacturersModal(): void {
    if (this.manufacturersModalCloseTimeout) {
      clearTimeout(this.manufacturersModalCloseTimeout);
      this.manufacturersModalCloseTimeout = undefined;
    }

    this.isManufacturersModalMounted = true;
    this.manufacturerSearchTerm = '';
    this.selectedManufacturerLetter = '';
    this.cdr.detectChanges();
    const firstFrame = requestAnimationFrame(() => {
      const secondFrame = requestAnimationFrame(() => {
        this.isManufacturersModalOpen = true;
        this.manufacturersModalOpenFrames = [];
        this.cdr.markForCheck();
      });
      this.manufacturersModalOpenFrames = [secondFrame];
    });
    this.manufacturersModalOpenFrames = [firstFrame];

    if (this.isLoadingManufacturers) {
      return;
    }

    this.isLoadingManufacturers = true;
    this.productsService.getManufacturers()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: manufacturers => {
          const manufacturersByName = new Map<string, string>();

          for (const value of manufacturers) {
            const name = String(value || '').trim().replace(/\s+/g, ' ');
            const normalizedName = name.toLocaleLowerCase();
            if (name) {
              manufacturersByName.set(normalizedName, name);
            }
          }

          this.manufacturers = [...manufacturersByName.values()]
            .sort((a, b) => a.localeCompare(b));
          this.isLoadingManufacturers = false;
          this.cdr.markForCheck();
        },
        error: error => {
          this.isLoadingManufacturers = false;
          this.cdr.markForCheck();
          console.error('Failed to load manufacturers', error);
        },
      });
  }

  selectManufacturer(name: string): void {
    this.closeManufacturersModal();
    this.router.navigate(['/products'], { queryParams: { manufacturer: name } });
  }

  private cancelManufacturersModalOpenFrames(): void {
    this.manufacturersModalOpenFrames.forEach(frame => cancelAnimationFrame(frame));
    this.manufacturersModalOpenFrames = [];
  }

  isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  navigateToLogin(): void {
    this.router.navigate(['/auth/login']);
  }

  navigateToRegister(): void {
    this.router.navigate(['/auth/register-multi']);
  }

  navigateToProfile(): void {
    if (this.isLoggedIn()) {
      this.router.navigate(['/profile']);
    } else {
      this.router.navigate(['/auth/login']);
    }
  }

  logout(): void {
    this.authService.logout();
    this.closeAuthDropdown();
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  toggleDrawer(): void {
    this.isDrawerOpen = !this.isDrawerOpen;
    if (this.isDrawerOpen) {
      this.selectedDesktopMenuAction = 'CATEGORIES';
      // Close cart and auth dropdown if open
      this.isCartOpen = false;
      this.isAuthDropdownOpen = false;
    }
  }

  onDesktopMenuAction(action: 'CATEGORIES' | 'SALE' | 'NEW'): void {
    if (this.isDrawerOpen && this.selectedDesktopMenuAction === action) {
      this.isDrawerOpen = false;
    } else {
      this.selectedDesktopMenuAction = action;
      this.isDrawerOpen = true;
      this.isCartOpen = false;
      this.isAuthDropdownOpen = false;
    }
    this.cdr.markForCheck();
  }

  closeDrawer(): void {
    this.isDrawerOpen = false;
  }

  private justOpenedCart = false;

  openCart(): void {
    this.isCartOpen = true;
    this.isCartClosing = false;
    this.isAuthDropdownOpen = false;
    this.isDrawerOpen = false;
    this.justOpenedCart = true;
    setTimeout(() => {
      this.justOpenedCart = false;
    }, 150);
    this.cdr.markForCheck();
  }

  toggleCart(): void {
    if (this.isCartOpen) {
      this.triggerCloseCart();
    } else {
      this.openCart();
    }
  }
  toggleMobileSearch() {
    this.isMobileSearchOpen = !this.isMobileSearchOpen;

    // Auto-focus search input when opening - using ViewChild for safer access
    if (this.isMobileSearchOpen) {
      setTimeout(() => {
        if (this.mobileSearchInput?.nativeElement) {
          this.mobileSearchInput.nativeElement.focus();
        }
      }, 100);
    }
  }

  toggleAuthDropdown(): void {
    this.isAuthDropdownOpen = !this.isAuthDropdownOpen;
    if (this.isAuthDropdownOpen) {
      this.triggerCloseCart();
      this.isDrawerOpen = false;
    }
  }

  closeAuthDropdown(): void {
    this.isAuthDropdownOpen = false;
  }

  closeCart(): void {
    this.triggerCloseCart();
  }

  triggerCloseCart(): void {
    if (!this.isCartOpen || this.isCartClosing) return;
    this.isCartClosing = true;
    setTimeout(() => {
      this.isCartOpen = false;
      this.isCartClosing = false;
    }, 370);
  }

  onProductClick(product: any): void {
    // Clear the search when a product is clicked
    this.searchControl.setValue('');
    this.filteredProducts = [];
    // Also close mobile search if open
    if (this.isMobileSearchOpen) {
      this.isMobileSearchOpen = false;
    }
  }

  onViewAllResults(searchQuery: string): void {
    // Clear the search when navigating to view all results
    this.searchControl.setValue('');
    this.filteredProducts = [];
    // Also close mobile search if open
    if (this.isMobileSearchOpen) {
      this.isMobileSearchOpen = false;
    }
  }

  private getProducts() {
    // No longer pre-fetching 1000 products for search
  }

  protected getSearchValue() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        startWith(this.searchControl.value),
        distinctUntilChanged(),
        switchMap((value) => {
          let rawQuery = (value as string || '').trim();
          if (!rawQuery || rawQuery.length < 2) return of({ data: [] });

          // Clean technical string
          const cleaned = rawQuery.replace(/\[Search\].*?\s/gi, '')
            .replace(/navbar\.component\.ts:\d+/gi, '')
            .replace(/\s+/g, ' ')
            .trim();

          // STAGE 1: Prioritize Reference (Unique identifier)
          return this.productsService.getProducts(1, 10, '', { reference: cleaned }).pipe(
            switchMap(rRef => {
              if (rRef?.data?.length > 0) return of(rRef);

              // STAGE 2: If query looks like a technical title "Name - Specs"
              if (cleaned.includes(' - ')) {
                const parts = cleaned.split(' - ');
                const coreName = parts[0].trim();
                const withOneSpec = parts.length > 1 ? `${parts[0]} - ${parts[1]}`.trim() : coreName;

                // Try with core name + one spec
                return this.productsService.getProducts(1, 10, '', { label: withOneSpec }).pipe(
                  switchMap(rSpec => {
                    if (rSpec?.data?.length > 0 && rSpec.data.length < 5) return of(rSpec);

                    // Then try core name
                    return this.productsService.getProducts(1, 10, '', { label: coreName }).pipe(
                      switchMap(rCore => {
                        if (rCore?.data?.length > 0) return of(rCore);

                        // Fallback to full string
                        return this.productsService.getProducts(1, 10, '', { label: cleaned });
                      })
                    );
                  })
                );
              }

              // STAGE 3: Standard label search
              return this.productsService.getProducts(1, 10, '', { label: cleaned }).pipe(
                switchMap(rLabel => {
                  if (rLabel?.data?.length > 0) return of(rLabel);

                  // STAGE 4: Word-based fallback for very long strings
                  const words = cleaned.split(/[\s\-\,]+/).filter(w => w.length > 2);
                  if (words.length > 2) {
                    const partialMatch = words.slice(0, 3).join(' ');
                    return this.productsService.getProducts(1, 10, '', { label: partialMatch });
                  }

                  return of(rLabel);
                })
              );
            }),
            catchError(() => of({ data: [] }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response: any) => {
        const products = response?.data ?? [];
        this.filteredProducts = products.map((product: any) => {
          // Reset variants for each product
          this.variants = {};
          // Process the variants
          this.setVariants(product?.variants);
          // Convert the variants object to an array of objects
          product.availableVariants = Object.values(this.variants);
          return product;
        });
        this.cdr.markForCheck();
      });
  }
  // filterProducts is no longer needed as search is now API-driven
  setVariants(variants: any) {
    variants.map((variant: any) => {
      if (!this.variants[variant?.variant?.id]) {
        this.variants[variant?.variant?.id] = {
          variant: variant?.variant,
          values: [variant],
        };
        this.selectedVariants[variant?.variant?.id] = variant;
      } else {
        this.variants[variant?.variant?.id].values.push(variant);
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.updateResponsiveStates();
  }

  @HostListener('window:scroll', ['$event'])
  @HostListener('document:scroll', ['$event'])
  onWindowScroll() {
    this.isScrolled = window.pageYOffset > 50 || document.documentElement.scrollTop > 50;
    
    // Close floating dropdowns on scroll for better UX
    if (this.isAuthDropdownOpen) {
      this.isAuthDropdownOpen = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.justOpenedCart) return;

    // Close dropdowns when clicking outside
    const target = event.target as HTMLElement;

    // Check if click is outside cart area
    if (
      this.isCartOpen &&
      !target.closest('app-cart-button') &&
      !target.closest('.cart-sidebar') &&
      !target.closest('.cart-backdrop') &&
      !target.closest('#toast-container') &&
      !target.closest('.ngx-toastr')
    ) {
      this.triggerCloseCart();
    }

    // Check if click is outside auth dropdown area
    if (this.isAuthDropdownOpen && !target.closest('.auth-button') && !target.closest('.auth-dropdown')) {
      this.isAuthDropdownOpen = false;
    }

    // Check if click is outside country selector area
    if (this.isCountrySelectorOpen && !target.closest('.country-selector-container') && !target.closest('[role="dialog"]') && !target.closest('.cookie-consent-overlay')) {
      this.isCountrySelectorOpen = false;
    }

    // Check if click is outside navbar country inner dropdown area
    if (this.isNavbarCountryDropdownOpen && !target.closest('.navbar-country-dropdown-container')) {
      this.closeNavbarCountryDropdown();
    }

    // Check if click is outside navbar currency inner dropdown area
    if (this.isNavbarCurrencyDropdownOpen && !target.closest('.navbar-currency-dropdown-container')) {
      this.closeNavbarCurrencyDropdown();
    }

    // Check if click is outside search area (both desktop and mobile)
    if ((this.searchControl.value && this.filteredProducts.length > 0) || this.isMobileSearchOpen) {
      const searchContainers = [
        '.search-container-wrapper',
        '.search-results-container',
        'app-search-results',
        '.mobile-search-overlay',
        '.search-input-container'
      ];

      const clickedInsideSearch = searchContainers.some(selector => target.closest(selector));

      if (!clickedInsideSearch) {
        // Close search dropdown
        if (this.searchControl.value) {
          this.searchControl.setValue('');
          this.filteredProducts = [];
        }
        // Close mobile search overlay
        if (this.isMobileSearchOpen) {
          this.isMobileSearchOpen = false;
        }
      }
    }

    // Check if click is outside drawer area
    if (
      this.isDrawerOpen
      && !target.closest('.nav-category-actions')
      && !target.closest('app-menu-toggle')
      && !target.closest('.drawer-content')
      && !target.closest('#mega-menu')
    ) {
      this.isDrawerOpen = false;
    }
  }

  private updateResponsiveStates() {
    this.isDesktop = window.innerWidth >= 1024;
    this.isMobile = window.innerWidth < 1024;

    // Close mobile-specific overlays on desktop
    if (this.isDesktop && this.isMobileSearchOpen) {
      this.isMobileSearchOpen = false;
    }
  }

  ngAfterViewInit() {
    this.updateResponsiveStates();

    // Auto-focus search input when mobile search opens - using ViewChild
    if (this.isMobileSearchOpen && this.mobileSearchInput?.nativeElement) {
      setTimeout(() => {
        this.mobileSearchInput?.nativeElement.focus();
      }, 100);
    }
  }

  // Country/Currency Selector Methods
  get filteredCountries() {
    if (!this.countrySearchTerm) {
      return this.countries;
    }
    const query = this.countrySearchTerm.toLowerCase();
    return this.countries.filter(country =>
      country.name.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  }

  toggleNavbarCountryDropdown(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isNavbarCountryDropdownOpen = !this.isNavbarCountryDropdownOpen;
    if (this.isNavbarCountryDropdownOpen) {
      this.closeNavbarCurrencyDropdown();
      this.countrySearchTerm = '';
      setTimeout(() => {
        this.navbarCountrySearchInputElement?.nativeElement?.focus();
      }, 50);
    }
  }

  closeNavbarCountryDropdown(): void {
    this.isNavbarCountryDropdownOpen = false;
    this.countrySearchTerm = '';
  }

  onCountrySearchInput(term: string): void {
    this.countrySearchTerm = term;
  }

  clearCountrySearchTerm(event: MouseEvent): void {
    event.stopPropagation();
    this.countrySearchTerm = '';
    this.navbarCountrySearchInputElement?.nativeElement?.focus();
  }

  onSelectCountryOption(country: any, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.countrySelectionMessage = '';
    this.selectCountry(country);
    this.closeNavbarCountryDropdown();
  }

  toggleNavbarCurrencyDropdown(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isNavbarCurrencyDropdownOpen = !this.isNavbarCurrencyDropdownOpen;
    if (this.isNavbarCurrencyDropdownOpen) {
      this.closeNavbarCountryDropdown();
    }
  }

  closeNavbarCurrencyDropdown(): void {
    this.isNavbarCurrencyDropdownOpen = false;
  }

  onSelectCurrencyOption(currency: SupportedCurrency, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.countrySelectionMessage = '';
    this.selectedCurrency = currency;
    this.closeNavbarCurrencyDropdown();
  }

  // Prevent double-firing on mobile devices
  onCountryButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    // Debounce rapid clicks
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
    }

    this.clickTimeout = setTimeout(() => {
      this.toggleCountrySelector();
    }, 50);
  }

  private clickTimeout: any;

  toggleCountrySelector(): void {
    // Debug log removed

    if (this.isCountrySelectorOpen) {
      // Smooth close with animation
      this.closeCountrySelector();
    } else {
      // Smooth open
      this.isCountrySelectorOpen = true;
      // Reset search term when opening
      this.countrySearchTerm = '';
      this.countrySelectionMessage = '';
    }

    // Debug log removed
  }

  closeCountrySelector(): void {
    // Add a slight delay to allow animations to complete
    this.isCountrySelectorOpen = false;
    setTimeout(() => {
      this.countrySearchTerm = '';
    }, 300);
  }

  selectCountry(country: any): void {
    this.countrySelectionMessage = '';
    this.selectedCountry = country;
    const code = (country?.code || '').toUpperCase();
    if (code === 'PL') {
      this.selectedCurrency = 'PLN';
    } else {
      this.selectedCurrency = 'EUR';
    }
  }

  onCurrencySelectChange(event: any): void {
    const val = event?.target?.value || event;
    if (val === 'PLN' || val === 'EUR') {
      this.countrySelectionMessage = '';
      this.selectedCurrency = val;
    }
  }

  onCountrySelectChange(event: any): void {
    const countryCode = event.target.value;
    const country = this.countries.find(c => c.code === countryCode);
    if (country) {
      this.selectCountry(country);
    }
  }

  async applyCountrySelection(): Promise<void> {
    if (this.selectedCountry) {
      const currency: SupportedCurrency = this.selectedCurrency || (this.selectedCountry.code === 'PL' ? 'PLN' : 'EUR');
      const savedCountry = localStorage.getItem('brocom_selected_country') || this.getCookie(this.COUNTRY_COOKIE_KEY);
      let savedCountryCode = '';

      if (savedCountry) {
        try {
          const parsed = JSON.parse(savedCountry);
          savedCountryCode = String(parsed?.code || parsed || '').toUpperCase();
        } catch {
          savedCountryCode = savedCountry.toUpperCase();
        }
      }

      if (
        savedCountryCode === String(this.selectedCountry.code || '').toUpperCase() &&
        currency === this.currencyService.currentCurrency
      ) {
        this.countrySelectionMessage = `Wybrany kraj i waluta są już ustawione: ${currency === 'PLN' ? 'PLN' : 'EUR'}.`;
        return;
      }

      this.countrySelectionMessage = '';
      // Save country selection to cookies
      this.setCookie(this.COUNTRY_COOKIE_KEY, JSON.stringify(this.selectedCountry), 365);
      this.setCookie(this.COUNTRY_VISITED_KEY, 'true', 365);

      // Save to localStorage for delivery country detection across the app
      localStorage.setItem('brocom_selected_country', JSON.stringify(this.selectedCountry));
      this.languageService.emitCountryChange(this.selectedCountry.code);

      // Save and apply chosen currency
      this.currencyService.setCurrency(currency, true);
      this.setCookie('brocom_selected_currency', currency, 365);
      localStorage.setItem('brocom_selected_currency', currency);

      // Close the selector
      this.closeCountrySelector();

      // Reload the page to apply the new country and currency across all components
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }

  // Cookie management methods
  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Apply language based on selected country (synchronous, no delay)
  private applyLanguageFromCountry(): void {
    // Language is now managed independently by the language switcher
    // No automatic language change based on country
  }

  private openCountrySelectorSmooth(): void {
    // Pre-animate the button to indicate something is happening
    const button = document.querySelector('.country-selector-container button');
    if (button) {
      button.classList.add('animate-pulse');
      setTimeout(() => button.classList.remove('animate-pulse'), 800);
    }

    // Smooth open with slight delay for better UX
    setTimeout(() => {
      this.isCountrySelectorOpen = true;
      this.countrySearchTerm = '';

      // Auto-detect user's country based on timezone or other methods
      this.detectUserCountry();
    }, 200);
  }

  private detectUserCountry(): void {
    // Try to detect user's country based on timezone
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let detectedCountry = null;

      // Simple timezone to country mapping
      const timezoneCountryMap: { [key: string]: string } = {
        'Africa/Tunis': 'TN',
        'Europe/Paris': 'FR',
        'Europe/Berlin': 'DE',
        'Europe/Rome': 'IT',
        'Europe/Madrid': 'ES',
        'Europe/London': 'GB',
        'America/New_York': 'US',
        'America/Toronto': 'CA',
        'Europe/Warsaw': 'PL',
        'Europe/Amsterdam': 'NL',
        'Europe/Brussels': 'BE',
        'Europe/Zurich': 'CH',
        'Europe/Vienna': 'AT',
        'Europe/Copenhagen': 'DK',
        'Europe/Stockholm': 'SE',
        'Europe/Oslo': 'NO',
        'Europe/Helsinki': 'FI'
      };

      const countryCode = timezoneCountryMap[timezone];
      if (countryCode) {
        detectedCountry = this.countries.find(country => country.code === countryCode);
      }

      // Fallback to Tunisia if no detection
      if (!detectedCountry) {
        detectedCountry = this.countries.find(country => country.code === 'TN');
      }

      if (detectedCountry) {
        this.selectedCountry = detectedCountry;
        // Save auto-detected country to localStorage for language detection
        localStorage.setItem('brocom_selected_country', JSON.stringify(detectedCountry));
      }
    } catch (error) {
      // Fallback to Tunisia
      const fallbackCountry = this.countries.find(country => country.code === 'TN') || this.countries[0];
      this.selectedCountry = fallbackCountry;
      localStorage.setItem('brocom_selected_country', JSON.stringify(fallbackCountry));
    }
  }

  // Get the current language flag based on the selected language
  getCurrentLanguageFlag(): string {
    const selectedLanguage = this.languageService.currentLanguage;

    // Return flag based on selected language
    if (selectedLanguage === 'fr') {
      return '🇫🇷';
    } else if (selectedLanguage === 'pl') {
      return '🇵🇱';
    } else {
      return '🇬🇧'; // English or default
    }
  }

  getCurrentLanguageCode(): string {
    return this.languageService.currentLanguage;
  }

  showPreviousTopBarAnnouncement(): void {
    if (this.topBarAnnouncements.length > 1) {
      this.activeTopBarIndex =
        (this.activeTopBarIndex - 1 + this.topBarAnnouncements.length) % this.topBarAnnouncements.length;
    }
  }

  showNextTopBarAnnouncement(): void {
    if (this.topBarAnnouncements.length > 1) {
      this.activeTopBarIndex = (this.activeTopBarIndex + 1) % this.topBarAnnouncements.length;
    }
  }

  private loadTopBarAnnouncements(language: 'en' | 'fr' | 'pl'): void {
    this.topBarRequest?.unsubscribe();
    this.topBarRequest = this.settingsService.getActiveTopBarAnnouncements(language)
      .pipe(take(1))
      .subscribe({
        next: announcements => {
          this.topBarAnnouncements = announcements;
          this.activeTopBarIndex = 0;
        },
        error: error => {
          console.error('Failed to load top bar announcements', error);
          this.topBarAnnouncements = [];
          this.activeTopBarIndex = 0;
        },
      });
  }
}
